/**
 * 注释管理Hook
 * 提供统一的注释查找和管理功能
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import commentManager, { CommentCache } from '../services/commentManager';

export interface UseCommentsOptions {
  gameTypeId: number;
  autoLoad?: boolean; // 是否自动加载注释
}

export interface UseCommentsReturn {
  comments: CommentCache;
  isLoading: boolean;
  error: string | null;
  findComment: (configFile: string, configSection: string, parameterKey: string) => { comment: string; description: string } | null;
  findCommentsBatch: (parameters: Array<{ configFile: string; configSection: string; parameterKey: string }>) => { [key: string]: { comment: string; description: string } };
  refreshComments: () => Promise<void>;
  isInitialized: boolean;
}

/**
 * 注释管理Hook
 * @param options 配置选项
 * @returns 注释管理相关的状态和方法
 */
export function useComments(options: UseCommentsOptions): UseCommentsReturn {
  const { gameTypeId, autoLoad = true } = options;
  const [comments, setComments] = useState<CommentCache>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // 使用ref来避免重复初始化
  const initializedRef = useRef(false);

  /**
   * 加载注释数据
   */
  const loadComments = async () => {
    if (isLoading || initializedRef.current) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      await commentManager.initialize(gameTypeId);
      const allComments = commentManager.getAllComments();
      
      setComments(allComments);
      setIsInitialized(true);
      initializedRef.current = true;
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load comments';
      setError(errorMessage);
      console.error('Failed to load comments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 刷新注释数据
   */
  const refreshComments = async () => {
    commentManager.clearCache();
    initializedRef.current = false;
    setIsInitialized(false);
    await loadComments();
  };

  /**
   * 查找单个参数注释
   */
  const findComment = (configFile: string, configSection: string, parameterKey: string) => {
    if (!isInitialized) return null;
    return commentManager.findComment(configFile, configSection, parameterKey);
  };

  /**
   * 批量查找参数注释
   */
  const findCommentsBatch = (parameters: Array<{ configFile: string; configSection: string; parameterKey: string }>) => {
    if (!isInitialized) return {};
    return commentManager.findCommentsBatch(parameters);
  };

  // 自动加载注释
  useEffect(() => {
    if (autoLoad && !initializedRef.current) {
      loadComments();
    }
  }, [autoLoad, gameTypeId]);

  // 当gameTypeId变化时重新加载
  useEffect(() => {
    if (gameTypeId && initializedRef.current) {
      refreshComments();
    }
  }, [gameTypeId]);

  return {
    comments,
    isLoading,
    error,
    findComment,
    findCommentsBatch,
    refreshComments,
    isInitialized
  };
}

/**
 * 简化的注释查找Hook
 * 只提供查找功能，不管理加载状态
 */
export function useCommentFinder(gameTypeId: number) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        await commentManager.initialize(gameTypeId);
        setIsReady(true);
      } catch (error) {
        console.error('Failed to initialize comment manager:', error);
      }
    };

    initialize();
  }, [gameTypeId]);

  const findComment = (configFile: string, configSection: string, parameterKey: string) => {
    if (!isReady) return null;
    return commentManager.findComment(configFile, configSection, parameterKey);
  };

  const findCommentsBatch = (parameters: Array<{ configFile: string; configSection: string; parameterKey: string }>) => {
    if (!isReady) return {};
    return commentManager.findCommentsBatch(parameters);
  };

  return {
    findComment,
    findCommentsBatch,
    isReady
  };
}

export default useComments;
